'use client'

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTRPC } from '@packages/utilities/hooks'
import { useQuery } from '@tanstack/react-query'

interface WatchlistContextValue {
  watchlistMonitorId: string | null
  setWatchlistMonitorId: (id: string | null) => void
}

const WatchlistContext = createContext<WatchlistContextValue | null>(null)

interface Properties {
  children: React.ReactNode
}

export const WatchlistProvider: React.FC<Properties> = ({ children }) => {
  const [watchlistMonitorId, setWatchlistMonitorId] = useState<string | null>(
    null
  )

  const value = useMemo<WatchlistContextValue>(
    () => ({ watchlistMonitorId, setWatchlistMonitorId }),
    [watchlistMonitorId]
  )

  return (
    <WatchlistContext.Provider value={value}>
      {children}
    </WatchlistContext.Provider>
  )
}

export function useWatchlistData() {
  const api = useTRPC()
  const context = useContext(WatchlistContext)

  const existingId = context?.watchlistMonitorId ?? null

  const queryOptions =
    api.shared.monitors.getWatchlistDocumentNumbers.queryOptions(
      { monitorId: existingId ?? undefined },
      {
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
      }
    )

  const { data, isPending } = useQuery(queryOptions)

  const returnedId = data?.watchlistId ?? null
  useEffect(() => {
    if (returnedId && returnedId !== existingId) {
      context?.setWatchlistMonitorId(returnedId)
    }
  }, [returnedId, existingId, context])

  const documentNumbers = data?.documentNumbers ?? []

  return {
    documentNumbers,
    pathKey: api.shared.monitors.getWatchlistDocumentNumbers.pathKey(),
    isLoading: isPending,
  }
}
